import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Alert,
  InputAdornment
} from '@mui/material'
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import TableLoading from 'components/TableLoading'
import {
  Account,
  addAdmin,
  deleteAccount,
  getAllAccounts,
  removeAdmin
} from 'api/adminManagement'

type ConfirmAction = 'add' | 'remove' | 'delete'

const AdminManagement = () => {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    account: Account | null
    action: ConfirmAction | null
  }>({
    open: false,
    account: null,
    action: null
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(0)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchAccounts = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await getAllAccounts({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined
      })
      setAccounts(result.accounts)
      setTotalCount(result.pagination.total)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch accounts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccounts()
  }, [page, rowsPerPage, debouncedSearch])

  const handleOpenConfirmDialog = (account: Account, action: ConfirmAction) => {
    setConfirmDialog({ open: true, account, action })
  }

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, account: null, action: null })
  }

  const handleConfirmAction = async () => {
    if (!confirmDialog.account || !confirmDialog.action) return

    try {
      setError(null)
      if (confirmDialog.action === 'add') {
        await addAdmin(confirmDialog.account.id)
        setSuccess(`${confirmDialog.account.email} added as admin successfully`)
      } else if (confirmDialog.action === 'remove') {
        await removeAdmin(confirmDialog.account.id)
        setSuccess(
          `Admin role removed from ${confirmDialog.account.email} successfully`
        )
      } else {
        await deleteAccount(confirmDialog.account.id)
        setSuccess(`${confirmDialog.account.email} deleted successfully`)
      }
      handleCloseConfirmDialog()
      fetchAccounts()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Operation failed')
    }
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <Container sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3 }}>
          <AdminIcon fontSize='large' color='primary' />
          <Typography variant='h4'>Admin Management</Typography>
        </Stack>

        <Box mb={3}>
          <TextField
            fullWidth
            placeholder='Search by email...'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity='success'
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {loading ? (
          <TableLoading />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                        No accounts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts.map(account => (
                      <TableRow key={account.id} hover>
                        <TableCell>{account.email}</TableCell>
                        <TableCell>
                          {account.firstName} {account.lastName}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.role === 'ADMIN' ? 'Admin' : 'User'}
                            size='small'
                            sx={{
                              bgcolor:
                                account.role === 'ADMIN'
                                  ? '#50A7EF'
                                  : '#2e7d32',
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(account.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          <Stack direction='row' spacing={0.5}>
                            {account.role === 'USER' ? (
                              <IconButton
                                size='small'
                                color='primary'
                                onClick={() =>
                                  handleOpenConfirmDialog(account, 'add')
                                }
                                title='Add as Admin'
                              >
                                <PersonAddIcon />
                              </IconButton>
                            ) : (
                              <IconButton
                                size='small'
                                color='warning'
                                onClick={() =>
                                  handleOpenConfirmDialog(account, 'remove')
                                }
                                title='Remove Admin'
                              >
                                <PersonRemoveIcon />
                              </IconButton>
                            )}
                            <IconButton
                              size='small'
                              color='error'
                              onClick={() =>
                                handleOpenConfirmDialog(account, 'delete')
                              }
                              title='Delete Account'
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>

      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog}>
        <DialogTitle>
          {confirmDialog.action === 'add'
            ? 'Add Admin'
            : confirmDialog.action === 'remove'
            ? 'Remove Admin'
            : 'Delete Account'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'add'
              ? `Are you sure you want to add ${confirmDialog.account?.email} as admin?`
              : confirmDialog.action === 'remove'
              ? `Are you sure you want to remove admin role from ${confirmDialog.account?.email}?`
              : `Are you sure you want to delete ${confirmDialog.account?.email}? Accounts with existing orders cannot be deleted.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            variant='contained'
            color={confirmDialog.action === 'add' ? 'primary' : 'error'}
          >
            {confirmDialog.action === 'add'
              ? 'Add Admin'
              : confirmDialog.action === 'remove'
              ? 'Remove Admin'
              : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default AdminManagement
